import React from 'react';
import { useSettings } from '../context/SettingsContext';
import { Box, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Typography, Paper } from '@mui/material';
import { parseDateFromString } from '../lib/settings';

const FirstOfficerTrainingSelector = ({ values, handleChange, showTrainingDay = false, isUpdateOnly = false }) => {
  const { getSafetyOfficerDates, isFirstOfficerTrainingDayPassed, isFirstOfficerTrainingAvailable, isLastFirstOfficerPoolSessionPassed } = useSettings();
  const firstOfficerDates = getSafetyOfficerDates().FIRST_OFFICER;
  const trainingDay = firstOfficerDates.TRAINING_DAY;
  const poolSessions = firstOfficerDates.POOL_SESSIONS || [];
  const trainingDayPassed = isFirstOfficerTrainingDayPassed();
  const trainingAvailable = isFirstOfficerTrainingAvailable();
  const poolSessionsPassed = isLastFirstOfficerPoolSessionPassed();

  // Helper function to check if a specific pool session date has passed
  const isSessionPassed = (sessionDate) => {
    const sessionDateObj = parseDateFromString(sessionDate);
    if (!sessionDateObj) return true;
    return new Date() > sessionDateObj;
  };

  // Filter pool sessions to only show future ones when updating
  const availablePoolSessions = isUpdateOnly 
    ? poolSessions.filter(session => !isSessionPassed(session.DATE))
    : poolSessions;

  // If showTrainingDay is true (user said "no" to prior training) and no training is available
  if (showTrainingDay && !trainingAvailable) {
    return (
      <Box sx={{ mt: 2 }}>
        <Paper sx={{ p: 2, backgroundColor: '#fff3cd', borderLeft: '4px solid #ffc107' }}>
          <Typography variant="body2" sx={{ color: '#856404' }}>
            Training is no longer available for this season. Please select a different duty type.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // If showTrainingDay is false (user said "yes" to prior training) and all pool sessions have passed
  if (!showTrainingDay && poolSessionsPassed) {
    // For update mode, show current session info without allowing changes
    if (isUpdateOnly && values.firstPoolSession) {
      return (
        <Box sx={{ mt: 2 }}>
          <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
            <Typography variant="body2" color="textSecondary">
              Your selected pool session: <strong>{values.firstPoolSession}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
              This session has passed and cannot be changed.
            </Typography>
          </Paper>
        </Box>
      );
    }
    return (
      <Box sx={{ mt: 2 }}>
        <Paper sx={{ p: 2, backgroundColor: '#fff3cd', borderLeft: '4px solid #ffc107' }}>
          <Typography variant="body2" sx={{ color: '#856404' }}>
            Only select this option if you are an existing 1st Safety Officer with a valid RLSS certificate.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {/* Show training day info only if showTrainingDay is true and it hasn't passed */}
      {showTrainingDay && !trainingDayPassed && (
        <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0d2c54', mb: 1 }}>
            Training Day
          </Typography>
          <Typography variant="body2" color="textSecondary">
            You will need to attend the training day on <strong>{trainingDay}</strong>
          </Typography>
        </Paper>
      )}

      {availablePoolSessions.length > 0 ? (
        <FormControl component="fieldset">
          <FormLabel component="legend">
            {isUpdateOnly ? 'Change your pool session (only future sessions shown):' : 'Please select which pool session you will attend:'}
          </FormLabel>
          <RadioGroup
            name="firstPoolSession"
            value={values.firstPoolSession || ''}
            onChange={handleChange}
          >
            {availablePoolSessions.map((session) => (
              <FormControlLabel
                key={session.DATE}
                value={`${session.DATE} at ${session.TIME}`}
                control={<Radio />}
                label={`${session.DATE} at ${session.TIME}`}
              />
            ))}
          </RadioGroup>
        </FormControl>
      ) : isUpdateOnly && values.firstPoolSession ? (
        <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
          <Typography variant="body2" color="textSecondary">
            Your selected pool session: <strong>{values.firstPoolSession}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
            No future pool sessions available to change to.
          </Typography>
        </Paper>
      ) : null}
    </Box>
  );
};

export default FirstOfficerTrainingSelector;
